/**
 * Tenant-aggregate React Query hooks.
 *
 * Covers the current tenant ("me"), tenant search, the data-ownership
 * editor, and sister/parent/child relationship management. Keys live
 * under ['tenants', ...].
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/hooks/useAppApi'
import { MY_TENANT_QUERY_KEY } from '@/platform/tenant/useMyTenant'
import type {
  TenantDto,
  UpdateTenantInput,
  UpdateDataOwnershipInput,
  LinkRelationshipInput,
  TenantRelationshipType,
} from '@shared/types/tenant'

// `useMyTenant` lives in `@/platform/tenant/useMyTenant` — the app shell needs
// current-tenant identity and must not import from a feature.

export function useTenantMutations() {
  const api = useApi()
  const qc = useQueryClient()

  const updateSelf = useMutation({
    mutationFn: (patch: UpdateTenantInput) => api.patch('/tenants/me', patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: MY_TENANT_QUERY_KEY }),
  })

  const updateDataOwnership = useMutation({
    mutationFn: ({ tenantId, patch }: { tenantId: string; patch: UpdateDataOwnershipInput }) =>
      api.put(`/admin/tenants/${tenantId}/data-ownership`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenants'] }),
  })

  const linkRelationship = useMutation({
    mutationFn: ({ tenantId, input }: { tenantId: string; input: LinkRelationshipInput }) =>
      api.post(`/admin/tenants/${tenantId}/relationships`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenants'] }),
  })

  const unlinkRelationship = useMutation({
    mutationFn: ({
      tenantId,
      relatedId,
      type,
    }: {
      tenantId: string
      relatedId: string
      type: TenantRelationshipType
    }) => api.delete(`/admin/tenants/${tenantId}/relationships/${relatedId}/${type}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenants'] }),
  })

  return { updateSelf, updateDataOwnership, linkRelationship, unlinkRelationship }
}

export function useTenantSearch(q: string, enabled: boolean) {
  const api = useApi()
  return useQuery<TenantDto[]>({
    queryKey: ['tenants', 'search', q],
    queryFn: () =>
      api.get(`/tenants/search?q=${encodeURIComponent(q)}`) as Promise<TenantDto[]>,
    enabled: enabled && q.trim().length >= 2,
    staleTime: 30_000,
  })
}
